import { useState, useEffect, useContext } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import Layout from '../components/Layout';
import { AuthContext } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { apiKeyService, type ApiKey } from '../services/apiKeyService';

export default function ApiKeysPage() {
  const { user: _user } = useContext(AuthContext);
  const { showToast } = useToast();
  const { t } = useLingui();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState('');
  const [creating, setCreating] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);

  useEffect(() => {
    loadKeys();
  }, []);

  const loadKeys = async () => {
    try {
      setLoading(true);
      const data = await apiKeyService.getKeys();
      setKeys(data);
    } catch (error: any) {
      showToast(error.response?.data?.error || t`Failed to load API keys`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;
    try {
      setCreating(true);
      const result = await apiKeyService.generateKey(newKeyName.trim());
      setCreatedKey(result.apiKey.key);
      setShowCreateModal(false);
      setShowKeyModal(true);
      setNewKeyName('');
      await loadKeys();
    } catch (error: any) {
      showToast(error.response?.data?.error || t`Failed to create API key`, 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (keyId: string) => {
    try {
      setRevoking(keyId);
      await apiKeyService.revokeKey(keyId);
      showToast(t`API key revoked`, 'success');
      await loadKeys();
    } catch (error: any) {
      showToast(error.response?.data?.error || t`Failed to revoke API key`, 'error');
    } finally {
      setRevoking(null);
    }
  };

  const handleCopyKey = () => {
    navigator.clipboard.writeText(createdKey);
    setKeyCopied(true);
    setTimeout(() => setKeyCopied(false), 2000);
  };

  const formatDate = (date: string | null) => {
    if (!date) return t`Never`;
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const activeKeys = keys.filter(k => k.status === 'active');
  const revokedKeys = keys.filter(k => k.status === 'revoked');

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text)' }}><Trans>Developer API Keys</Trans></h1>
            <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
              <Trans>
                Generate API keys for third-party integrations using the PrivacyPal SDK. Usage is metered by sensitive elements protected.
              </Trans>
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm whitespace-nowrap"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <Trans>Generate New Key</Trans>
          </button>
        </div>

        {/* Active Keys */}
        <div className="glass-panel rounded-2xl border p-5" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)' }}>
          <h2 className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ color: 'var(--color-text-muted)' }}>
            <Trans>Active Keys ({activeKeys.length})</Trans>
          </h2>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="spinner" />
            </div>
          ) : activeKeys.length === 0 ? (
            <div className="text-center py-8">
              <svg className="w-12 h-12 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
              </svg>
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                <Trans>No active API keys. Generate one to get started with the SDK.</Trans>
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b" style={{ borderColor: 'var(--color-border)' }}>
                    <th className="text-left py-2 pr-4 font-medium" style={{ color: 'var(--color-text-muted)' }}><Trans>Name</Trans></th>
                    <th className="text-left py-2 pr-4 font-medium" style={{ color: 'var(--color-text-muted)' }}><Trans>Key</Trans></th>
                    <th className="text-right py-2 pr-4 font-medium" style={{ color: 'var(--color-text-muted)' }}><Trans>Requests</Trans></th>
                    <th className="text-right py-2 pr-4 font-medium" style={{ color: 'var(--color-text-muted)' }}><Trans>Elements Protected</Trans></th>
                    <th className="text-left py-2 pr-4 font-medium" style={{ color: 'var(--color-text-muted)' }}><Trans>Last Used</Trans></th>
                    <th className="text-left py-2 pr-4 font-medium" style={{ color: 'var(--color-text-muted)' }}><Trans>Created</Trans></th>
                    <th className="text-right py-2 font-medium" style={{ color: 'var(--color-text-muted)' }}><Trans>Actions</Trans></th>
                  </tr>
                </thead>
                <tbody>
                  {activeKeys.map(key => (
                    <tr key={key.id} className="border-b last:border-0" style={{ borderColor: 'var(--color-border)' }}>
                      <td className="py-3 pr-4 font-medium" style={{ color: 'var(--color-text)' }}>{key.name}</td>
                      <td className="py-3 pr-4">
                        <code className="text-xs px-2 py-1 rounded" style={{ backgroundColor: 'var(--color-surface-secondary)', color: 'var(--color-text-secondary)' }}>
                          {key.keyPrefix}...
                        </code>
                      </td>
                      <td className="py-3 pr-4 text-right tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>{key.totalRequests.toLocaleString()}</td>
                      <td className="py-3 pr-4 text-right tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>{key.totalElements.toLocaleString()}</td>
                      <td className="py-3 pr-4 text-xs" style={{ color: 'var(--color-text-muted)' }}>{formatDate(key.lastUsedAt)}</td>
                      <td className="py-3 pr-4 text-xs" style={{ color: 'var(--color-text-muted)' }}>{formatDate(key.createdAt)}</td>
                      <td className="py-3 text-right">
                        <button
                          onClick={() => handleRevoke(key.id)}
                          disabled={revoking === key.id}
                          className="text-xs px-3 py-1.5 rounded-md border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors disabled:opacity-50"
                        >
                          {revoking === key.id ? <Trans>Revoking...</Trans> : <Trans>Revoke</Trans>}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Revoked Keys */}
        {revokedKeys.length > 0 && (
          <div className="glass-panel rounded-2xl border p-5" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)' }}>
            <h2 className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ color: 'var(--color-text-muted)' }}>
              <Trans>Revoked Keys ({revokedKeys.length})</Trans>
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b" style={{ borderColor: 'var(--color-border)' }}>
                    <th className="text-left py-2 pr-4 font-medium" style={{ color: 'var(--color-text-muted)' }}><Trans>Name</Trans></th>
                    <th className="text-left py-2 pr-4 font-medium" style={{ color: 'var(--color-text-muted)' }}><Trans>Key</Trans></th>
                    <th className="text-right py-2 pr-4 font-medium" style={{ color: 'var(--color-text-muted)' }}><Trans>Total Requests</Trans></th>
                    <th className="text-right py-2 pr-4 font-medium" style={{ color: 'var(--color-text-muted)' }}><Trans>Total Elements</Trans></th>
                    <th className="text-left py-2 font-medium" style={{ color: 'var(--color-text-muted)' }}><Trans>Revoked</Trans></th>
                  </tr>
                </thead>
                <tbody>
                  {revokedKeys.map(key => (
                    <tr key={key.id} className="border-b last:border-0 opacity-60" style={{ borderColor: 'var(--color-border)' }}>
                      <td className="py-3 pr-4 line-through" style={{ color: 'var(--color-text-secondary)' }}>{key.name}</td>
                      <td className="py-3 pr-4">
                        <code className="text-xs px-2 py-1 rounded line-through" style={{ backgroundColor: 'var(--color-surface-secondary)', color: 'var(--color-text-muted)' }}>
                          {key.keyPrefix}...
                        </code>
                      </td>
                      <td className="py-3 pr-4 text-right tabular-nums" style={{ color: 'var(--color-text-muted)' }}>{key.totalRequests.toLocaleString()}</td>
                      <td className="py-3 pr-4 text-right tabular-nums" style={{ color: 'var(--color-text-muted)' }}>{key.totalElements.toLocaleString()}</td>
                      <td className="py-3 text-xs" style={{ color: 'var(--color-text-muted)' }}>{formatDate(key.revokedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>

      {/* Create Key Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="rounded-xl p-6 max-w-md w-full mx-4 shadow-xl" style={{ backgroundColor: 'var(--color-surface)' }}>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--color-text)' }}><Trans>Generate New API Key</Trans></h2>
            <p className="text-sm mb-4" style={{ color: 'var(--color-text-secondary)' }}>
              <Trans>Give your key a descriptive name so you can identify it later.</Trans>
            </p>
            <input
              type="text"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              placeholder={t`e.g. Production Backend, Staging App`}
              maxLength={64}
              className="w-full px-4 py-2.5 rounded-lg text-sm mb-4"
              style={{ backgroundColor: 'var(--color-surface-secondary)', border: '1px solid var(--color-border)', color: 'var(--color-text)' }}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              autoFocus
            />
            <div className="flex gap-3">
              <button
                onClick={() => { setShowCreateModal(false); setNewKeyName(''); }}
                className="flex-1 px-4 py-2.5 rounded-lg transition-colors text-sm font-medium"
                style={{ backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text)' }}
              >
                <Trans>Cancel</Trans>
              </button>
              <button
                onClick={handleCreate}
                disabled={!newKeyName.trim() || creating}
                className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50"
              >
                {creating ? <Trans>Generating...</Trans> : <Trans>Generate Key</Trans>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Key Created Modal */}
      {showKeyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="rounded-xl p-6 max-w-lg w-full mx-4 shadow-xl" style={{ backgroundColor: 'var(--color-surface)' }}>
            <div className="flex items-center gap-2 mb-4">
              <svg className="w-6 h-6 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h2 className="text-xl font-bold" style={{ color: 'var(--color-text)' }}><Trans>API Key Created</Trans></h2>
            </div>
            <div className="p-3 rounded-lg border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-500/10 mb-4">
              <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
                <Trans>Copy this key now. For security, it will not be displayed again.</Trans>
              </p>
            </div>
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={createdKey}
                readOnly
                className="flex-1 px-3 py-2 rounded-lg text-xs font-mono"
                style={{ backgroundColor: 'var(--color-surface-secondary)', border: '1px solid var(--color-border)', color: 'var(--color-text)' }}
              />
              <button
                onClick={handleCopyKey}
                className="px-3 py-2 rounded-lg border transition-colors flex-shrink-0"
                style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }}
              >
                {keyCopied ? (
                  <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                )}
              </button>
            </div>
            <button
              onClick={() => { setShowKeyModal(false); setCreatedKey(''); setKeyCopied(false); }}
              className="w-full px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <Trans>Done</Trans>
            </button>
          </div>
        </div>
      )}
    </Layout>
  );
}
